import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Trash2,
  Eye,
  Loader2,
} from 'lucide-react';
import { Pill } from '@/components/ui/pill';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { fmtBRL, fmtCpfCnpj, fmtDate } from '@/lib/utils';
import {
  useDeleteDocumentoFiscal,
  type DocumentoFiscal,
  type TipoDocumento,
} from '@/lib/hooks/documentos-fiscais';

interface Props {
  workspaceId: string;
  data: DocumentoFiscal[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  onPageChange: (offset: number) => void;
}

type TipoTone = 'mint' | 'lilac' | 'info' | 'default';

const TIPO_TONE: Record<TipoDocumento, TipoTone> = {
  NFSE: 'mint',
  NFE: 'lilac',
  NFAE: 'info',
  OUTRO: 'default',
};

const TIPO_LABEL: Record<TipoDocumento, string> = {
  NFSE: 'NFS-e',
  NFE: 'NF-e',
  NFAE: 'NFA-e',
  OUTRO: 'Outro',
};

export function DocumentosFiscaisTable({
  workspaceId,
  data,
  total,
  limit,
  offset,
  loading,
  onPageChange,
}: Props) {
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DocumentoFiscal | null>(null);
  const deleteMut = useDeleteDocumentoFiscal(workspaceId);

  function askDelete(doc: DocumentoFiscal) {
    setOpenRow(null);
    setConfirmDelete(doc);
  }

  async function confirmDeleteNow() {
    if (!confirmDelete) return;
    try {
      await deleteMut.mutateAsync(confirmDelete.id);
      toast.success('Documento fiscal removido.');
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover.');
    }
  }

  const columns = useMemo<ColumnDef<DocumentoFiscal>[]>(
    () => [
      {
        accessorKey: 'dataEmissao',
        header: 'Data',
        cell: ({ row }) => (
          <span className="text-sm text-fg whitespace-nowrap">
            {fmtDate(row.original.dataEmissao)}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: 'tipo',
        header: 'Tipo',
        cell: ({ row }) => {
          const tipo = row.original.tipo as TipoDocumento;
          return (
            <Pill tone={TIPO_TONE[tipo] ?? 'default'} dot>
              {TIPO_LABEL[tipo] ?? tipo}
            </Pill>
          );
        },
        size: 90,
      },
      {
        id: 'numeroSerie',
        header: 'Número / Série',
        cell: ({ row }) => {
          const { numero, serie } = row.original;
          if (!numero && !serie) return <span className="text-sm text-fg-muted">—</span>;
          return (
            <div className="min-w-0">
              <p className="text-sm font-medium text-fg">{numero ?? '—'}</p>
              {serie && <p className="text-[11px] text-fg-muted font-mono">série {serie}</p>}
            </div>
          );
        },
        size: 120,
      },
      {
        accessorKey: 'emitenteNome',
        header: 'Emitente',
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg truncate">{row.original.emitenteNome}</p>
            <p className="text-[11px] text-fg-muted font-mono">
              {fmtCpfCnpj(row.original.emitenteCnpj)}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'valorTotal',
        header: () => <span className="block text-right">Valor</span>,
        cell: ({ row }) => (
          <span className="block text-right font-mono text-sm font-semibold text-fg tabular-nums">
            {fmtBRL(row.original.valorTotal)}
          </span>
        ),
        size: 130,
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Acoes</span>,
        cell: ({ row }) => {
          const doc = row.original;
          const isOpen = openRow === doc.id;
          return (
            <div className="relative flex justify-end">
              <button
                type="button"
                onClick={() => setOpenRow(isOpen ? null : doc.id)}
                className="grid h-8 w-8 place-items-center rounded-1 text-fg-muted hover:text-fg hover:bg-card-soft"
                aria-label="Acoes"
              >
                <MoreHorizontal className="h-4 w-4" strokeWidth={1.8} />
              </button>
              {isOpen && (
                <>
                  <div
                    className="fixed inset-0 z-30"
                    onClick={() => setOpenRow(null)}
                    aria-hidden
                  />
                  <div className="absolute right-0 top-9 z-40 w-44 rounded-2 border border-card-stroke bg-card shadow-clay p-1">
                    {doc.r2Key && (
                      <button
                        type="button"
                        onClick={() => setOpenRow(null)}
                        className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-fg hover:bg-card-soft"
                      >
                        <Eye className="h-4 w-4" strokeWidth={1.8} />
                        Ver nota fiscal
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => askDelete(doc)}
                      className="flex w-full items-center gap-2 rounded-1 px-2 py-1.5 text-sm text-warn hover:bg-warn-soft"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                      Excluir
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        },
        size: 60,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openRow, deleteMut.isPending],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.floor(offset / limit) + 1;
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div className="rounded-3 border border-card-stroke bg-card shadow-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-card-stroke bg-card-soft/60">
                {hg.headers.map((h) => (
                  <th
                    key={h.id}
                    style={{ width: h.column.columnDef.size }}
                    className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted"
                  >
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-fg-muted">
                  <Loader2 className="inline h-4 w-4 animate-spin mr-2" strokeWidth={1.8} />
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && table.getRowModel().rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-10 text-center text-fg-muted text-sm"
                >
                  Nenhum documento fiscal encontrado.
                </td>
              </tr>
            )}
            {!loading &&
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-card-stroke/50 last:border-b-0 hover:bg-card-soft/40 transition-colors"
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 align-middle">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        tone="danger"
        title="Excluir este documento fiscal?"
        description={
          confirmDelete
            ? `${confirmDelete.emitenteNome} · ${fmtBRL(confirmDelete.valorTotal)}. Esta acao nao pode ser desfeita.`
            : undefined
        }
        confirmLabel="Excluir"
        loading={deleteMut.isPending}
        onConfirm={confirmDeleteNow}
      />

      {total > 0 && (
        <div className="flex items-center justify-between gap-2 border-t border-card-stroke px-4 py-3 bg-card-soft/40">
          <p className="text-xs text-fg-muted">
            Pagina <span className="text-fg font-medium">{currentPage}</span> de{' '}
            <span className="text-fg font-medium">{totalPages}</span> · {total}{' '}
            {total === 1 ? 'documento' : 'documentos'}
          </p>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canPrev}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!canNext}
              onClick={() => onPageChange(offset + limit)}
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.8} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
